import { useCallback } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { ExtendedMember } from 'src/constants/types';
import MemberCard from 'src/components/memberCard';
import LastEditedBy from 'src/modules/community/singlePost/components/lastEditedBy';
import { showLoginModal, showSignupModal } from 'src/modules/shared/modal/actions';
import { trackFootfallEvent, trackFootfallEventWithHrefDelay } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { useModalTrigger } from 'src/utils/hooks';
import { ModalType } from 'src/constants/modalTypes';
import { textSmall } from 'styles-js/mixins/typography';

export default function PostAuthor() {
  const dispatch = useDispatch();
  const post = useSelector(({ pages }) => pages.singlePost.post);
  const currentUser = useSelector(({ context })=> context.currentUser);

  const { age, bio, country, gender, healthInterests, avatar, roles, username, id } = post.author;
  const isUserAuthor = (currentUser?.userId === id);
  const isAuthorEmptyProfile = isUserAuthor ?
    !age && !bio && !country && !gender && (healthInterests?.length === 0) : false;

  const member: ExtendedMember = {
    age: age,
    bio: bio,
    country: country,
    gender: gender,
    healthInterests: healthInterests,
    profileImageSmall2x: avatar,
    roles: roles,
    username: username,
  };

  const router = useRouter();
  const onCloseModal = () => {
    router.reload();
  };

  const triggerProfileSurveyModal = useModalTrigger(ModalType.ProfileSurvey, { onCloseModal });
  const clickOnCardProfileSurvey = useCallback(() => {
    trackFootfallEvent(Event.Clicked, { clickType: 'profile-survey-author', clickMetadata: { section: Section.MemberCardSpp } });
    triggerProfileSurveyModal();
  }, []);

  const clickOnCardJoinOrLogin = (type?: string) => {
    if (type === 'login') {
      trackFootfallEvent(Event.Clicked, {
        clickType: 'login-member-card', clickMetadata: { section: Section.MemberCardSpp },
      });
      dispatch(showLoginModal());
    } else {
      trackFootfallEvent(Event.Clicked, {
        clickType: 'join-member-card', clickMetadata: { section: Section.MemberCardSpp },
      });
      dispatch(showSignupModal());
    }
  };

  const clickOnMemberCard = (href: string, username: string) => {
    if (username === currentUser?.username) {
      trackFootfallEventWithHrefDelay('/profile/edit', Event.Clicked, {
        clickType: 'clicked-by-author', clickMetadata: { section: Section.MemberCardSpp },
      });
    } else {
      trackFootfallEventWithHrefDelay(href, Event.Clicked, {
        clickType: 'clicked-by-other', clickMetadata: { section: Section.MemberCardSpp },
      });
    }
  };

  const clickOnDropdown = () => {
    if (isUserAuthor) {
      trackFootfallEvent(Event.Clicked, { clickType: 'dropdown-by-author', clickMetadata: { section: Section.MemberCardSpp } });
    } else {
      trackFootfallEvent(Event.Clicked, { clickType: 'dropdown-by-other', clickMetadata: { section: Section.MemberCardSpp } });
    }
  };

  return (
    <>
      {!post.author.isDeleted && (
        <>
          <WrittenBy className={'written-by'}>{i18n.t('Written by')}</WrittenBy>
          { !!currentUser && !isAuthorEmptyProfile ? (
            <MemberCard
              isExpandable={true}
              member={member}
              onClick={clickOnMemberCard}
              onDropdownClick={clickOnDropdown}
            />
          ) : (
            <MemberCard
              isAuthorEmptyProfile={isAuthorEmptyProfile}
              isUserLoggedIn={!!currentUser}
              member={member}
              onClick={clickOnMemberCard}
              onLinkClick={isUserAuthor ? clickOnCardProfileSurvey : clickOnCardJoinOrLogin}
            />
          )}
        </>
      )}
      {post.editAuthor && <LastEditedBy editAuthor={post.editAuthor} />}
    </>
  );
}

const WrittenBy = styled.div`
  ${textSmall};
  font-weight: 800;
  color: ${({ theme }) => theme.colorTextLight};
`;
